// Required environment variables (add to .env and GitHub Actions secrets):
//   REACT_APP_STRIPE_SOLO_PRICE_ID  — Stripe price ID for the Solo plan
//   REACT_APP_STRIPE_BAND_PRICE_ID  — Stripe price ID for the Band plan

import React, { useState, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { SOLO_MODAL, BAND_MODAL } from "../features/upgrade/upgradeModalContent";

const CONTENT_MAP = { solo: SOLO_MODAL, band: BAND_MODAL };

function priceIdForTier(tier) {
  return tier === "solo"
    ? process.env.REACT_APP_STRIPE_SOLO_PRICE_ID
    : process.env.REACT_APP_STRIPE_BAND_PRICE_ID;
}

function SoloIcon() {
  return (
    <svg
      className="w-7 h-7 text-brand-gold"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
    </svg>
  );
}

function BandIcon() {
  return (
    <svg
      className="w-7 h-7 text-brand-gold"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8.5" cy="8" r="3.5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M1.5 20c0-3.5 3.1-5.8 7-5.8s7 2.3 7 5.8" />
      <path d="M17.5 14c2.5 0 5 1.5 5 4" />
    </svg>
  );
}

export default function UpgradeModal({ tier, onClose, tenantContext }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const panel = modalRef.current;
    if (!panel) return;

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => [...panel.querySelectorAll(focusableSelector)];

    getFocusable()[0]?.focus();

    function onKeyDown(e) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = getFocusable();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!tier) return null;

  const content = CONTENT_MAP[tier];
  const isBand = tier === "band";
  const bandId = tenantContext?.bandId;

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const createCheckoutSession = httpsCallable(
        functions,
        "createCheckoutSession"
      );
      const result = await createCheckoutSession({
        priceId: priceIdForTier(content.tier),
        bandId,
      });
      const url = result.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error("Checkout error:", err);
      setLoading(false);
      setError("Something went wrong. Please try again.");
    }
  };

  const accentText = "text-brand-gold";
  const bulletAccent = isBand ? "text-brand-gold" : "text-accent-green";
  const topBorder = "border-t-brand-gold";
  const ctaBg = "bg-brand-gold text-[#0d0d0c] hover:bg-brand-gold/85";

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/75 px-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-heading"
        className={[
          "relative w-full max-w-[440px] bg-surface border border-white/[0.11]",
          "border-t-2 rounded-sm px-11 py-12 shadow-[0_24px_80px_rgba(0,0,0,0.8)]",
          topBorder,
        ].join(" ")}
      >
        <button
          className="absolute top-4 right-4 bg-transparent border-0 text-text-muted hover:text-text-primary text-xl leading-none px-1.5 py-0.5 cursor-pointer transition-colors"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="mb-4" aria-hidden="true">
          {isBand ? <BandIcon /> : <SoloIcon />}
        </div>

        <div className={`mb-3 font-sans text-[0.7rem] tracking-[0.2em] uppercase ${accentText}`}>
          {isBand ? "Band Plan" : "Solo Plan"}
        </div>

        <h2
          id="upgrade-modal-heading"
          className="font-display text-[1.75rem] font-normal leading-[1.2] tracking-[-0.02em] text-text-primary mb-3.5"
        >
          {content.headline}
        </h2>

        <p className="text-text-secondary text-[0.95rem] leading-[1.65] mb-5">
          {content.subheading}
        </p>

        <ul className="mb-5 space-y-2.5 list-none p-0">
          {content.bullets.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2.5 text-[0.85rem] text-text-secondary"
            >
              <span className={`mt-0.5 shrink-0 leading-none ${bulletAccent}`}>
                ✓
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p className="text-text-secondary text-[0.82rem] font-bold tracking-[0.02em] mb-5">
          {content.closingLine}
        </p>

        {error && (
          <p role="alert" className="text-[#e05c5c] text-[0.85rem] mb-4">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2.5">
          <button
            className={[
              "w-full font-bold border-0 rounded-sm py-3.5 text-[0.78rem]",
              "tracking-[0.15em] uppercase transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              ctaBg,
            ].join(" ")}
            disabled={loading}
            onClick={handleCheckout}
          >
            {loading ? "Redirecting…" : content.ctaLabel}
          </button>

          <button
            className="bg-transparent border-0 text-text-muted text-[0.82rem] underline cursor-pointer pt-1.5 hover:text-text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed w-full text-center"
            onClick={onClose}
            disabled={loading}
          >
            Stay on free for now
          </button>
        </div>
      </div>
    </div>
  );
}
